import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { logger } from '@/services/loggerService';

/**
 * Configuration options for the {@link useScreenRecorder} hook.
 */
type UseScreenRecorderOptions = {
  /**
   * Optional callback invoked every time a new media chunk is available
   * from the underlying {@link MediaRecorder}.
   *
   * Use this to stream the recording (e.g. upload progressively) instead of,
   * or in addition to, waiting for the final file in {@link onComplete}.
   */
  onChunk?: (chunk: Blob) => void;
  /**
   * Optional callback invoked once the recording has fully stopped and the
   * final file has been assembled.
   *
   * @param file - The recorded media as a {@link File} (typically WebM).
   * @param durationSeconds - Total recording duration in seconds.
   */
  onComplete?: (file: File, durationSeconds: number) => void;
  /**
   * Whether to include microphone audio in the recording.
   *
   * Defaults to `true`. Set to `false` to capture only system / screen audio
   * (when available) without mic input.
   */
  includeMic?: boolean;
  /**
   * Preferred capture frame rate, expressed in frames per second.
   *
   * This value is treated as a hint; the browser may choose a different
   * effective frame rate depending on capabilities and system load.
   * Common values are `30` (default) or `60` for smoother recordings.
   */
  preferredFps?: number;
  /**
   * Base name used when constructing the final recording filename.
   *
   * Typically used as the prefix before any timestamp or extension is added,
   * e.g. `recording-2024-01-01T12-00-00.webm` when `filenameBase` is
   * `"recording"`. Defaults to `"recording"`.
   */
  filenameBase?: string;
  /**
   * Hard cap on recording length in seconds. When reached the recorder stops
   * itself and finalizes normally (same as the user pressing Stop).
   *
   * `0` (default) disables the cap. Both sibling recorders auto-stop at their
   * plan limit; without this a user could record far past their limit only to
   * have the upload rejected afterwards, losing the whole session.
   */
  maxDuration?: number;
};

/**
 * Result returned by the {@link useScreenRecorder} hook, exposing current
 * recorder state and control methods.
 */
type UseScreenRecorderResult = {
  /**
   * Indicates whether a recording session is currently active.
   */
  isRecording: boolean;
  /**
   * Indicates whether the current recording is paused.
   *
   * This is `true` only while a recording is in progress and has been
   * paused via {@link pauseRecording}.
   */
  isPaused: boolean;
  /**
   * Elapsed recording time in milliseconds.
   *
   * Resets when a new recording starts and stops updating once the
   * recording has been stopped.
   */
  elapsedMs: number;
  /**
   * Last error message encountered by the recorder, or `null` if no error
   * has occurred.
   */
  error: string | null;
  /**
   * Indicates whether screen recording is supported in the current
   * environment (e.g. browser and permissions).
   */
  isSupported: boolean;
  /**
   * Prompts the user to select a screen/window to capture and returns the
   * display stream. Call this before a countdown so the picker appears first,
   * then pass the result to {@link startRecording}.
   */
  requestDisplayAccess: () => Promise<MediaStream>;
  /**
   * Starts a new screen recording session.
   *
   * If a pre-acquired display stream is provided (from {@link requestDisplayAccess}),
   * it is used directly — skipping the browser picker dialog.
   */
  startRecording: (preAcquiredDisplayStream?: MediaStream) => Promise<void>;
  /**
   * Pauses the current recording, if one is active and not already paused.
   */
  pauseRecording: () => void;
  /**
   * Resumes a paused recording.
   */
  resumeRecording: () => void;
  /**
   * Stops the current recording.
   *
   * Triggers finalization of the recorded data and, if provided, the
   * {@link UseScreenRecorderOptions.onComplete} callback.
   */
  stopRecording: () => void;
  /**
   * Stops and throws away the current recording WITHOUT firing
   * {@link UseScreenRecorderOptions.onComplete}.
   *
   * Use this for Discard/Restart. Calling {@link stopRecording} there would
   * finalize the file and trigger the caller's auto-upload — i.e. "Discard"
   * would upload the recording the user just chose to throw away.
   */
  discardRecording: () => void;
  /**
   * Clears any error message currently stored in the error state.
   */
  clearError: () => void;
};

/**
 * List of supported MIME types for screen recording, in order of preference.
 * Export for reuse by other components that need to check MIME type support.
 */
export const SUPPORTED_MIME_TYPES = [
  'video/webm;codecs=vp8,opus',
  'video/webm;codecs=vp8',
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp9',
  'video/webm',
];

/**
 * Timer update interval in milliseconds.
 * The timer updates every 200ms to provide smooth elapsed time display
 * without excessive re-renders.
 */
const TIMER_UPDATE_INTERVAL_MS = 200;

/**
 * Default video bitrate for recordings in bits per second (6 Mbps).
 * This provides good quality for 1080p screen recordings while keeping
 * file sizes manageable.
 */
const DEFAULT_VIDEO_BITRATE = 6_000_000;

/**
 * Picks the first supported MIME type from {@link SUPPORTED_MIME_TYPES}.
 *
 * Returns `undefined` if running server-side (SSR-safe) or if no supported
 * MIME types are available in the current browser.
 */
const pickMime = (): string | undefined => {
  // SSR-safe: return undefined if window or MediaRecorder is not available
  if (typeof window === 'undefined' || typeof MediaRecorder === 'undefined') {
    return undefined;
  }

  return SUPPORTED_MIME_TYPES.find((type) => {
    try {
      return MediaRecorder.isTypeSupported(type);
    } catch {
      return false;
    }
  });
};

/**
 * Custom React hook for screen recording with MediaRecorder.
 *
 * This hook encapsulates the complete lifecycle of screen recording including:
 * - MediaRecorder setup and lifecycle management
 * - Audio stream mixing (system audio + microphone)
 * - Recording timer with pause/resume support
 * - Error handling and cleanup
 *
 * @example
 * ```tsx
 * function MyRecorder() {
 *   const {
 *     isRecording,
 *     isPaused,
 *     elapsedMs,
 *     error,
 *     startRecording,
 *     pauseRecording,
 *     resumeRecording,
 *     stopRecording,
 *   } = useScreenRecorder({
 *     onComplete: (file, duration) => {
 *       console.log('Recording complete:', file.name, duration);
 *     },
 *     includeMic: true,
 *     preferredFps: 30,
 *   });
 *
 *   return (
 *     <div>
 *       <button onClick={startRecording} disabled={isRecording}>Start</button>
 *       <button onClick={pauseRecording} disabled={!isRecording || isPaused}>Pause</button>
 *       <button onClick={resumeRecording} disabled={!isPaused}>Resume</button>
 *       <button onClick={stopRecording} disabled={!isRecording}>Stop</button>
 *       <p>Time: {Math.floor(elapsedMs / 1000)}s</p>
 *       {error && <p>Error: {error}</p>}
 *     </div>
 *   );
 * }
 * ```
 *
 * @param options - Configuration options for the recorder
 * @returns Recorder state and control functions
 *
 * @remarks
 * - Parent components should memoize `onChunk` and `onComplete` callbacks
 *   (e.g. with `useCallback`) to avoid unnecessary re-renders
 * - The hook automatically cleans up all resources on unmount
 * - Audio mixing may fail in some environments; the hook falls back to
 *   microphone-only audio in such cases
 */
export const useScreenRecorder = ({
  onChunk,
  onComplete,
  includeMic = true,
  preferredFps = 30,
  filenameBase = 'recording',
  maxDuration = 0,
}: UseScreenRecorderOptions): UseScreenRecorderResult => {
  const recorderRef = useRef<MediaRecorder | null>(null);
  const displayStreamRef = useRef<MediaStream | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const mixedStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  /**
   * Cloned audio tracks fed into the mixing AudioContext. Clones are independent
   * of their source track, so stopping the display/mic streams does NOT stop
   * them — without tracking them here the mic indicator stays lit after the
   * recording ends.
   */
  const clonedAudioTracksRef = useRef<MediaStreamTrack[]>([]);
  /** When true, the pending `onstop` finalizes and drops the file instead of emitting it. */
  const discardRef = useRef(false);

  // Use refs for callbacks to avoid recreating startRecording on every render
  const onChunkRef = useRef(onChunk);
  const onCompleteRef = useRef(onComplete);

  // Update refs when callbacks change
  useEffect(() => {
    onChunkRef.current = onChunk;
  }, [onChunk]);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const startTimestampRef = useRef<number | null>(null);
  const accumulatedTimeRef = useRef<number>(0);
  const timerIntervalRef = useRef<number | null>(null);

  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const mimeType = useMemo(() => pickMime(), []);
  const isSupported = useMemo(() => typeof mimeType === 'string', [mimeType]);

  const clearTimer = useCallback(() => {
    if (timerIntervalRef.current !== null) {
      window.clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  }, []);

  const updateElapsed = useCallback(() => {
    const start = startTimestampRef.current;
    if (start === null) {
      setElapsedMs(accumulatedTimeRef.current);
      return accumulatedTimeRef.current;
    }

    const total = accumulatedTimeRef.current + (performance.now() - start);
    setElapsedMs(total);
    return total;
  }, []);

  // Keep the cap in a ref so changing plan limits mid-session can't leave the
  // interval closed over a stale value.
  const maxDurationRef = useRef(maxDuration);
  useEffect(() => {
    maxDurationRef.current = maxDuration;
  }, [maxDuration]);

  /**
   * Timer tick: refresh elapsed time and auto-stop once the plan cap is hit,
   * so an over-limit recording is never produced in the first place.
   */
  const tick = useCallback(() => {
    const total = updateElapsed();
    const capMs = maxDurationRef.current * 1000;
    if (capMs > 0 && total >= capMs) {
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        try {
          recorder.stop();
        } catch (err) {
          logger.warn('video', 'Failed to auto-stop MediaRecorder at max duration', err);
        }
      }
    }
  }, [updateElapsed]);

  const beginTimer = useCallback(() => {
    startTimestampRef.current = performance.now();
    accumulatedTimeRef.current = 0;
    setElapsedMs(0);
    clearTimer();
    timerIntervalRef.current = window.setInterval(tick, TIMER_UPDATE_INTERVAL_MS);
  }, [clearTimer, tick]);

  const pauseTimer = useCallback(() => {
    if (startTimestampRef.current !== null) {
      accumulatedTimeRef.current += performance.now() - startTimestampRef.current;
      startTimestampRef.current = null;
    }
    clearTimer();
    updateElapsed();
  }, [clearTimer, updateElapsed]);

  const resumeTimer = useCallback(() => {
    if (startTimestampRef.current === null) {
      startTimestampRef.current = performance.now();
      clearTimer();
      timerIntervalRef.current = window.setInterval(tick, TIMER_UPDATE_INTERVAL_MS);
    }
  }, [clearTimer, tick]);

  const finalizeTimer = useCallback(() => {
    let total = accumulatedTimeRef.current;
    if (startTimestampRef.current !== null) {
      total += performance.now() - startTimestampRef.current;
    }

    accumulatedTimeRef.current = 0;
    startTimestampRef.current = null;
    clearTimer();
    const finalMs = total;
    setElapsedMs(0);
    return finalMs;
  }, [clearTimer]);

  const getDisplayStream = useCallback(async () => {
    const constraints: DisplayMediaStreamOptions = {
      video: {
        frameRate: preferredFps,
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        displaySurface: 'browser',
      },
      audio: true,
    };

    return navigator.mediaDevices.getDisplayMedia(constraints);
  }, [preferredFps]);

  const getMicStream = useCallback(async () => {
    if (!includeMic) {
      return null;
    }

    try {
      return await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch (err) {
      // Microphone access denied or unavailable - continue without mic audio
      logger.warn('video', 'Microphone access failed, continuing without mic', err);
      return null;
    }
  }, [includeMic]);

  const mixStreams = useCallback(
    async (displayStream: MediaStream, micStream: MediaStream | null): Promise<MediaStream> => {
      if (!micStream || micStream.getAudioTracks().length === 0) {
        return displayStream;
      }

      const mixed = new MediaStream();
      displayStream.getVideoTracks().forEach((track) => mixed.addTrack(track));

      try {
        // Close existing AudioContext before creating a new one to prevent memory leak
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
          await audioContextRef.current.close().catch(() => undefined);
        }

        const audioContext = new AudioContext();
        audioContextRef.current = audioContext;
        await audioContext.resume().catch(() => undefined);

        const destination = audioContext.createMediaStreamDestination();

        const pipeTracks = (tracks: MediaStreamTrack[]) => {
          tracks.forEach((track) => {
            // Track every clone — a clone survives its source being stopped, so
            // cleanupStreams has to stop these explicitly or the mic stays live.
            const clone = track.clone();
            clonedAudioTracksRef.current.push(clone);
            const audioOnlyStream = new MediaStream([clone]);
            audioContext.createMediaStreamSource(audioOnlyStream).connect(destination);
          });
        };

        if (displayStream.getAudioTracks().length > 0) {
          pipeTracks(displayStream.getAudioTracks());
        }

        if (micStream.getAudioTracks().length > 0) {
          pipeTracks(micStream.getAudioTracks());
        }

        const mixedAudioTracks = destination.stream.getAudioTracks();
        if (mixedAudioTracks.length > 0) {
          mixedAudioTracks.forEach((track) => mixed.addTrack(track));
          return mixed;
        }
      } catch (err) {
        // Audio mixing failed - fall back to mic track
        logger.warn('video', 'Audio mixing failed, falling back to microphone track only', err);
      }

      // Fallback: add microphone track directly so at least voice is captured
      micStream.getAudioTracks().forEach((track) => mixed.addTrack(track));
      return mixed;
    },
    [],
  );

  const cleanupStreams = useCallback(() => {
    displayStreamRef.current?.getTracks().forEach((track) => track.stop());
    micStreamRef.current?.getTracks().forEach((track) => track.stop());
    mixedStreamRef.current?.getTracks().forEach((track) => track.stop());
    // Clones fed to the AudioContext are separate tracks and keep the mic
    // capture indicator lit unless stopped here.
    clonedAudioTracksRef.current.forEach((track) => track.stop());
    clonedAudioTracksRef.current = [];
    audioContextRef.current?.close().catch(() => undefined);

    displayStreamRef.current = null;
    micStreamRef.current = null;
    mixedStreamRef.current = null;
    audioContextRef.current = null;
  }, []);

  const handleStop = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      return;
    }

    try {
      recorder.stop();
    } catch (err) {
      // Recorder stop may fail if already stopped or in invalid state
      logger.warn('video', 'Failed to stop MediaRecorder', err);
    }
  }, []);

  const startRecording = useCallback(
    async (preAcquiredDisplayStream?: MediaStream) => {
      if (!isSupported || isRecording) {
        return;
      }

      setError(null);
      chunksRef.current = [];
      // Disarm any stale discard flag. handleStop() swallows a throwing
      // recorder.stop(), in which case `onstop` never fires to consume the flag —
      // leaving it armed would silently drop the user's NEXT recording.
      discardRef.current = false;

      if (!mimeType) {
        setError('Screen recording is not supported in this browser.');
        return;
      }

      try {
        const displayStream = preAcquiredDisplayStream || (await getDisplayStream());
        const micStream = await getMicStream();

        displayStreamRef.current = displayStream;
        micStreamRef.current = micStream;

        const stream = await mixStreams(displayStream, micStream);
        mixedStreamRef.current = stream;

        const recorder = new MediaRecorder(stream, {
          mimeType,
          videoBitsPerSecond: DEFAULT_VIDEO_BITRATE,
        });

        recorderRef.current = recorder;

        recorder.ondataavailable = (event) => {
          if (event.data && event.data.size > 0) {
            chunksRef.current.push(event.data);
            onChunkRef.current?.(event.data);
          }
        };

        recorder.onerror = (err) => {
          logger.warn('video', 'MediaRecorder error', err);
          setError('Recording error. Please stop and try again.');
        };

        // Store cleanup function for event listener
        let removeTrackEndedListener: (() => void) | null = null;

        recorder.onstop = () => {
          const totalElapsedMs = finalizeTimer();
          const capSeconds = maxDurationRef.current;
          // Clamp to the cap. The 200ms timer tick trips the auto-stop at
          // cap+0..200ms and finalizeTimer() measures later still, so an
          // unclamped value is ALWAYS over the limit — the page would then
          // reject its own auto-stopped recording as too long and the whole
          // session would be unrecoverable. Both sibling recorders clamp too.
          const rawSeconds = Math.max(0, totalElapsedMs / 1000);
          const durationSeconds = capSeconds > 0 ? Math.min(rawSeconds, capSeconds) : rawSeconds;

          // Discard/Restart stop the recorder too — suppress onComplete so the
          // caller's auto-upload effect can't fire for a recording the user
          // explicitly threw away. Consume the flag either way.
          const discarded = discardRef.current;
          discardRef.current = false;

          if (!discarded) {
            const blob = new Blob(chunksRef.current, { type: mimeType });
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            const file = new File([blob], `${filenameBase}_${timestamp}.webm`, { type: mimeType });

            onCompleteRef.current?.(file, durationSeconds);
          }

          // Clean up event listener
          if (removeTrackEndedListener) {
            removeTrackEndedListener();
          }

          chunksRef.current = [];
          cleanupStreams();
          recorderRef.current = null;
          setIsRecording(false);
          setIsPaused(false);
        };

        // Set up video track ended listener with proper cleanup
        const [videoTrack] = displayStream.getVideoTracks();
        if (videoTrack) {
          const handleTrackEnded = () => {
            handleStop();
          };
          videoTrack.addEventListener('ended', handleTrackEnded);

          // Store cleanup function
          removeTrackEndedListener = () => {
            videoTrack.removeEventListener('ended', handleTrackEnded);
          };
        }

        recorder.start();
        beginTimer();
        setIsRecording(true);
        setIsPaused(false);
      } catch (err) {
        logger.warn('video', 'Failed to start screen capture', err);
        setError(err instanceof Error ? err.message : 'Unable to start screen recording.');
        cleanupStreams();
        recorderRef.current = null;
        chunksRef.current = [];
      }
    },
    [
      beginTimer,
      cleanupStreams,
      finalizeTimer,
      getDisplayStream,
      getMicStream,
      handleStop,
      isRecording,
      isSupported,
      mixStreams,
      mimeType,
      filenameBase,
    ],
  );

  const pauseRecording = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== 'recording') {
      return;
    }

    recorder.pause();
    pauseTimer();
    setIsPaused(true);
  }, [pauseTimer]);

  const resumeRecording = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== 'paused') {
      return;
    }

    recorder.resume();
    resumeTimer();
    setIsPaused(false);
  }, [resumeTimer]);

  const discardRecording = useCallback(() => {
    const recorder = recorderRef.current;

    if (!recorder || recorder.state === 'inactive') {
      // Nothing in flight, so no `onstop` will fire to consume the flag. Reset
      // everything here — leaving the flag armed would silently throw away the
      // user's NEXT recording.
      discardRef.current = false;
      chunksRef.current = [];
      finalizeTimer();
      cleanupStreams();
      recorderRef.current = null;
      setIsRecording(false);
      setIsPaused(false);
      return;
    }

    discardRef.current = true;
    handleStop();
  }, [cleanupStreams, finalizeTimer, handleStop]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Store cleanup functions in refs to avoid recreating effect on every render
  const handleStopRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    handleStopRef.current = handleStop;
  }, [handleStop]);

  // Cleanup effect - runs only on unmount
  useEffect(() => {
    return () => {
      // Detach handlers BEFORE stopping. `stop()` finalizes asynchronously, so a
      // still-attached `onstop` can fire after unmount and call onComplete with
      // nothing left mounted to receive the file — the sibling recorders guard
      // the same way.
      const recorder = recorderRef.current;
      if (recorder) {
        recorder.onstop = null;
        recorder.ondataavailable = null;
      }
      if (handleStopRef.current) {
        handleStopRef.current();
      }
      clearTimer();
      cleanupStreams();
    };
  }, [cleanupStreams, clearTimer]);

  return {
    isRecording,
    isPaused,
    elapsedMs,
    error,
    isSupported,
    requestDisplayAccess: getDisplayStream,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording: handleStop,
    discardRecording,
    clearError,
  };
};
